if (!customElements.get('video-modal-player')) {
    customElements.define(
      'video-modal-player',
      class BenefitsVideoModalPlayer extends HTMLElement {
        connectedCallback() {
            this.SectionId = this.dataset.benefitsVideoSectionId;

            if (!this.SectionId) return;

            if (this.parentElement !== document.body) {
                const existingPlayerForSection = document.querySelector(
                    `video-modal-player[data-benefits-video-section-id="${this.SectionId}"]`
                );

                if (existingPlayerForSection && existingPlayerForSection !== this) {
                    existingPlayerForSection.remove();
                }

                document.body.appendChild(this);
                return;
            }

            if (this.initialized) return;

            this.section = Array.from(document.querySelectorAll('video-reels[data-section-id]')).find(
                (section) => section.dataset.sectionId === this.SectionId,
            );
            this.modal = this.querySelector('[data-benefits-video-modal]');
            this.mediaContainer = this.querySelector('[data-benefits-video-modal-media]');

            if (!this.section || !this.modal || !this.mediaContainer) return;

            this.handleSectionClick = this.handleSectionClick.bind(this);
            this.handleModalClick = this.handleModalClick.bind(this);
            this.handleKeydown = this.handleKeydown.bind(this);

            this.section.addEventListener('click', this.handleSectionClick);
            this.modal.addEventListener('click', this.handleModalClick);
            this.initialized = true;
        }

        disconnectedCallback() {
            if (this.section && this.handleSectionClick) {
                this.section.removeEventListener('click', this.handleSectionClick);
            }

            if (this.modal && this.handleModalClick) {
                this.modal.removeEventListener('click', this.handleModalClick);
            }

            if (this.handleKeydown) {
                document.removeEventListener('keydown', this.handleKeydown);
            }

            this.closeModal({ restoreFocus: false });
            this.initialized = false;
        }

        handleSectionClick(event) {
            const trigger = event.target.closest('[data-benefits-video-trigger]');
            if (!trigger || !this.section.contains(trigger)) return;

            event.preventDefault();
            event.stopPropagation();
            this.openModal(trigger);
        }

        handleModalClick(event) {
            if (event.target.closest('[data-benefits-video-close]')) {
                this.closeModal();
            }
        }

        handleKeydown(event) {
            if (event.key === 'Escape') {
                this.closeModal();
            }
        }

        getSources(trigger) {
            try {
                const sources = JSON.parse(trigger.dataset.benefitsVideoSources || '[]');
                return Array.isArray(sources) ? sources : [];
            } catch (error) {
                return [];
            }
        }

        buildHostedVideo(trigger) {
            const sources = this.getSources(trigger);
            if (!sources.length) return null;

            const video = document.createElement('video');
            video.className = 'benefits-video-modal__video';
            video.controls = true;
            video.autoplay = true;
            video.loop = true;
            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.preload = 'metadata';

            sources.forEach((sourceData) => {
                if (!sourceData.src) return;

                const source = document.createElement('source');
                source.src = sourceData.src;

                if (sourceData.type) {
                    source.type = sourceData.type;
                }

                video.appendChild(source);
            });

            return video;
        }

        buildYoutubeVideo(trigger) {
            const youtubeId = trigger.dataset.benefitsVideoYoutubeId;
            if (!youtubeId) return null;

            const iframe = document.createElement('iframe');
            iframe.className = 'benefits-video-modal__video';
            iframe.src = `https://www.youtube.com/embed/${encodeURIComponent(youtubeId)}?autoplay=1&rel=0&modestbranding=1`;
            iframe.title = trigger.getAttribute('aria-label') || 'Video';
            iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
            iframe.allowFullscreen = true;
            iframe.setAttribute('frameborder', '0');

            return iframe;
        }

        buildMedia(trigger) {
            if (trigger.dataset.benefitsVideoProvider === 'youtube') {
                return this.buildYoutubeVideo(trigger);
            }

            return this.buildHostedVideo(trigger);
        }

        stopModalVideo() {
            const video = this.mediaContainer?.querySelector('video');
            if (!video) return;
            video.pause();
            video.currentTime = 0;
        }

        restoreFocusBeforeHide(restoreFocus) {
            const activeElement = document.activeElement;

            if (!activeElement || !this.modal?.contains(activeElement)) return;

            if (restoreFocus && this.previouslyFocused && document.contains(this.previouslyFocused)) {
                this.previouslyFocused.focus({ preventScroll: true });
                return;
            }

            activeElement.blur();
        }

        closeModal(options = {}) {
            const { restoreFocus = true } = options;

            this.stopModalVideo();
            this.restoreFocusBeforeHide(restoreFocus);

            if (this.mediaContainer) {
                this.mediaContainer.innerHTML = '';
            }

            if (this.modal) {
                this.modal.hidden = true;
                this.modal.setAttribute('aria-hidden', 'true');
            }

            document.body.classList.remove('benefits-video-modal-open');

            if (this.handleKeydown) {
                document.removeEventListener('keydown', this.handleKeydown);
            }
        }

        openModal(trigger) {
            const media = this.buildMedia(trigger);
            if (!media) return;

            this.section.querySelectorAll('.video-container video').forEach((sectionVideo) => {
                sectionVideo.pause();
            });

            this.mediaContainer.innerHTML = '';
            this.mediaContainer.appendChild(media);
            this.previouslyFocused = trigger;
            this.modal.hidden = false;
            this.modal.setAttribute('aria-hidden', 'false');
            document.body.classList.add('benefits-video-modal-open');
            this.modal.querySelector('.benefits-video-modal__close')?.focus({ preventScroll: true });

            if (typeof media.play === 'function') {
                media.play().catch(() => {});
            }

            document.addEventListener('keydown', this.handleKeydown);
        }
      }
    );
}
